import { formatBytes } from "../utilities/formatBytes";
import "../Download/DownloadSidebar.css";

const DownloadSidebar = ({
  setDownloadProgress,
  downloadProgress = {},
  downloadImages = [],
  isCollapsed = false,
  setIsCollapsed = () => {},
  setDownloadImages = () => {},
  setIsCheck = () => {},
  setIsCheckAll = () => {},
  isDownloadSidebarVisible = false,
  setIsDownloadSidebarVisible = () => {},
}) => {
  const getImageString = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    if (typeof item === "object" && typeof item.image === "string")
      return item.image;
    return "";
  };

  const parseFile = (item) => {
    const imageString = getImageString(item);
    const [fileurl = "", fileName = "Unknown File", ext = "", fileSize = ""] =
      imageString.split("$");
    return { fileurl, fileName, ext, fileSize };
  };

  const getInfo = (item, index) => {
    const { fileurl } = parseFile(item);
    return downloadProgress[fileurl] ?? downloadProgress[index];
  };

  const extractProgress = (info) => {
    // Normalize to { percent?, status?, error? }
    if (info == null) return { percent: null, status: null, error: null };

    if (typeof info === "number") {
      return {
        percent: info,
        status: info === 100 ? "completed" : "in-progress",
        error: null,
      };
    }

    if (typeof info === "object") {
      // explicit status takes precedence
      if (info.status === "failed") {
        return {
          percent: info.percent ?? null,
          status: "failed",
          error: info.error ?? "Failed",
        };
      }
      if (info.status === "completed") {
        return { percent: 100, status: "completed", error: null };
      }

      // derive from received/total
      if (
        typeof info.received === "number" &&
        typeof info.total === "number" &&
        info.total > 0
      ) {
        const p = Math.round((info.received / info.total) * 100);
        return {
          percent: p,
          status: p === 100 ? "completed" : "in-progress",
          error: null,
        };
      }

      if (typeof info.percent === "number") {
        return {
          percent: info.percent,
          status: info.percent === 100 ? "completed" : "in-progress",
          error: null,
        };
      }
    }

    return { percent: null, status: null, error: null };
  };

  const statusCounts = downloadImages.reduce(
    (acc, item, index) => {
      const { percent, status } = extractProgress(getInfo(item, index));
      if (status === "failed") acc.failed += 1;
      else if (status === "completed" || percent === 100) acc.completed += 1;
      else if (status === "in-progress" || (percent != null && percent < 100))
        acc.inProgress += 1;
      else acc.pending += 1; // no info yet
      return acc;
    },
    { inProgress: 0, completed: 0, failed: 0, pending: 0 },
  );

  const total = downloadImages.length;
  let headerText = "Download List";
  if (total > 0) {
    if (statusCounts.inProgress > 0) {
      headerText = `Downloading ${statusCounts.inProgress} item${statusCounts.inProgress === 1 ? "" : "s"}`;
    } else if (
      statusCounts.failed > 0 &&
      statusCounts.completed + statusCounts.failed === total
    ) {
      headerText = `Finished with ${statusCounts.failed} failed`;
    } else if (statusCounts.completed === total) {
      headerText = `Downloaded ${total} item${total === 1 ? "" : "s"}`;
    } else {
      headerText = `Selected ${total} item${total === 1 ? "" : "s"}`;
    }
  }

  const handleClearDownloads = () => {
    setIsCheck([]);
    setIsCheckAll(false);
    setDownloadImages([]);
    setDownloadProgress({});
  };
  return (
    <div
      className={`download-sidebar ${isCollapsed ? "collapsed" : "expanded"}`}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <button
          className="collapse-button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "18px",
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
            fontWeight: "bold",
            color: "#007bff",
            width: isCollapsed ? "100%" : "auto",
            justifyContent: isCollapsed ? "center" : "flex-end",
          }}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? "<<" : ">>"}
        </button>
        {!isCollapsed && (
          <p style={{ fontSize: "14px", fontWeight: "bold", margin: 0 }}>
            {headerText}
          </p>
        )}

        {!isCollapsed &&
          (downloadImages.length === 0 ? (
            <button
              className="close-downloads-button"
              onClick={() => setIsDownloadSidebarVisible(false)}
              title="Close"
              aria-label="Close download sidebar"
              style={{
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                color: "#ff4d4f",
                fontWeight: "bold",
              }}
            >
               ✘
            </button>
          ) : (
            <button
              className="clear-downloads-button"
              onClick={handleClearDownloads}
              title="Clear Downloads"
            >
              Clear
            </button>
          ))}
      </div>

      <div className="download-sidebar-content" aria-hidden={isCollapsed}>
        {downloadImages.length === 0 ? (
          <div style={{ marginLeft: "26%", color: "#666" }}>
            No downloads in progress.
          </div>
        ) : (
          downloadImages.map((item, index) => {
            const { fileurl, fileName, ext, fileSize } = parseFile(item);
            const info = getInfo(item, index);
            const { percent, status, error } = extractProgress(info);

            const isFailed = status === "failed";
            const isComplete =
              !isFailed && (status === "completed" || percent === 100);

            const fullName = ext ? `${fileName}.${ext}` : fileName;
            const percentDisplay =
              percent != null && !isFailed ? `${percent}%` : "";

            const sizeNum = Number(fileSize);
            const sizeDisplay =
              Number.isFinite(sizeNum) && sizeNum > 0
                ? formatBytes(sizeNum)
                : "";

            return (
              <div
                key={fileurl || index}
                style={{ marginBottom: "14px", opacity: isComplete ? 0.7 : 1 }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    wordBreak: "break-word",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{fullName}</span>

                  {isComplete && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#2e7d32",
                        fontWeight: "bold",
                      }}
                    >
                      ✓ Complete
                    </span>
                  )}

                  {isFailed && (
                    <span
                      title={error || "Failed"}
                      style={{
                        fontSize: "12px",
                        color: "#d32f2f",
                        fontWeight: "bold",
                      }}
                    >
                      ✘ Failed
                    </span>
                  )}
                </div>

                {/* progress bar: hide/gray when failed */}
                <div
                  style={{
                    background: "#eee",
                    height: "8px",
                    borderRadius: "4px",
                    marginTop: "6px",
                    overflow: "hidden",
                    opacity: isFailed ? 0.3 : 1,
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(0, Math.min(100, percent || 0))}%`,
                      background: isFailed
                        ? "#d32f2f"
                        : isComplete
                          ? "#2e7d32"
                          : "#4caf50",
                      height: "100%",
                      transition: "width 0.2s linear",
                    }}
                  />
                </div>

                <small
                  style={{
                    marginTop: "6px",
                    display: "flex",
                    justifyContent: "space-between",
                    color: isFailed ? "#d32f2f" : "#444",
                    fontSize: "12px",
                  }}
                >
                  <span>{isFailed ? error || "Failed" : percentDisplay}</span>
                  <span>{sizeDisplay}</span>
                </small>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DownloadSidebar;
